import { AnalyticsPlugin } from 'analytics';

import { rudderTyperClient } from './libraries';
import {
  RudderstackAnaltyicsConfig,
  ProductViewedPayload,
  ProductAddedPayload,
  ProductRemovedPayload,
  PageViewPayload,
} from './types';
import { ConsentCategory, ShopConfig } from '../../consent';
import { LoggerSkeleton } from '../../types';

function RudderstackAnalyticsPlugin(configParam: RudderstackAnaltyicsConfig): AnalyticsPlugin {
  const clientWindow: Window = configParam?.window;
  const logger: LoggerSkeleton = configParam?.logger;
  const shopConfig: ShopConfig = configParam?.shopConfig;
  clientWindow.rudderanalytics = [];

  const requiredConsentCategory: ConsentCategory = ConsentCategory.TARGETING;
  let isLoaded = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getRudderAnalytics(): any {
    return clientWindow.rudderanalytics;
  }

  function initialize(): void {
    try {
      logger.debug('class=RudderstackAnalyticsPlugin | #initialize');

      const methods = [
        'load',
        'page',
        'track',
        'identify',
        'alias',
        'group',
        'ready',
        'reset',
        'getAnonymousId',
        'setAnonymousId',
      ];

      for (let i = 0; i < methods.length; i++) {
        const method: string = methods[i];
        getRudderAnalytics()[method] = (function (methodName) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return function (...args: any[]) {
            getRudderAnalytics().push([methodName].concat(Array.prototype.slice.call(args)));
          };
        })(method);
      }

      const appKey = shopConfig.rudderStackId;

      getRudderAnalytics().load(appKey, 'https://allbirds-dataplane.rudderstack.com', {
        cookieConsentManager: {
          oneTrust: {
            enabled: true,
          },
        },
      });

      getRudderAnalytics().ready(onLoadedSuccess);

      const stubScript = clientWindow.document.createElement('script');
      stubScript.src = 'https://cdn.rudderlabs.com/v1.1/rudder-analytics.min.js';
      stubScript.type = 'text/javascript';

      clientWindow.document.head.appendChild(stubScript);
    } catch (e) {
      logger.error(
        'class=RudderstackAnalyticsPlugin | #initialize | error loading rudderstack plug-in',
        {},
        e
      );
    }
  }

  function onLoadedSuccess(): void {
    logger.debug('class=RudderstackAnalyticsPlugin | #onLoadedSuccess | rudderstack loaded');
    isLoaded = true;
  }

  // needed as part of the analytics library
  function loaded(): boolean {
    return isLoaded;
  }

  function page(payload: PageViewPayload): void {
    logger.debug('class=RudderstackAnalyticsPlugin | #page', { payload });
    getRudderAnalytics().page(payload?.pageData, '', payload?.meta);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function identify({ payload }: any): void {
    logger.info('class=RudderstackAnalyticsPlugin | #idenfity', { payload });
    getRudderAnalytics().identify(payload?.userId, payload?.traits, payload?.options);
  }

  function productViewed(payload: ProductViewedPayload): void {
    logger.info('class=RudderstackAnalyticsPlugin | #productViewed', { payload });
    rudderTyperClient.setRudderTyperOptions({
      analytics: clientWindow.rudderanalytics,
    });

    rudderTyperClient.productViewed({ ...payload.product, ...payload.meta });
  }

  function productRemoved(payload: ProductRemovedPayload): void {
    logger.info('class=RudderstackAnalyticsPlugin | #productRemoved', { payload });
    rudderTyperClient.setRudderTyperOptions({
      analytics: clientWindow.rudderanalytics,
    });

    rudderTyperClient.productRemoved({
      ...payload.removedProduct,
      ...payload.meta,
      products: payload.cartProducts,
    });
  }

  function productAdded(payload: ProductAddedPayload): void {
    logger.info('class=RudderstackAnalyticsPlugin | #productAdded', { payload });
    rudderTyperClient.setRudderTyperOptions({
      analytics: clientWindow.rudderanalytics,
    });

    rudderTyperClient.productAdded({
      products: payload.cartProducts,
      ...payload.addedProduct,
      ...payload.meta,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function track({ payload }: any): void {
    logger.info('class=RudderstackAnalyticsPlugin | #track', { payload });
    getRudderAnalytics().track(payload?.event, payload?.properties);
  }

  return {
    name: 'rudderstack',
    enabled: false,
    config: Object.freeze(configParam),
    initialize,
    loaded,
    page,
    requiredConsentCategory,
    track,
    identify,
    methods: {
      productViewed,
      productAdded,
      productRemoved,
      page,
    },
  };
}

export default RudderstackAnalyticsPlugin;
